import { CoreLogService } from "../log.service";
import { DirectoryPathProvider, PlatformDetector } from "./comEnvironmentPathConverter";

/** UAPP Environment FileSystem Interface
 * [reference/cordova-plugin-file](https://cordova.apache.org/docs/en/latest/reference/cordova-plugin-file/)
 */
export interface IFileSystem {
  // Prüfung des EnvDir
  isReadOnly(env_dir: EnvDir): boolean;
  // Directories & Files
  getAllEntries(env_dir: EnvDir, dir: string): Promise<Entry[]>;
  // Directories
  getDirectory(env_dir: EnvDir, dir: string): Promise<DirectoryEntry>;
  getAllSubDirectories(env_dir: EnvDir, dir: string): Promise<DirectoryEntry[]>;
  createDirectory(env_dir: EnvDir, dir: string): Promise<DirectoryEntry>;
  removeDirectory(env_dir: EnvDir, dir: string): Promise<boolean>;
  removeAllSubDirectories(env_dir: EnvDir, dir: string): Promise<boolean>;
  // Files
  getFile(env_dir: EnvDir, dir: string, file: string): Promise<FileEntry>;
  getAllFiles(env_dir: EnvDir, dir: string): Promise<FileEntry[]>;
  createFile(env_dir: EnvDir, dir: string, file: string): Promise<FileEntry>;
  removeFile(env_dir: EnvDir, dir: string, file: string): Promise<boolean>;
  removeAllFiles(env_dir: EnvDir, dir: string): Promise<boolean>;
  /** löscht mehrere dateien in einem ordner, dessen namen einem regex filter entsprechen (geht prüft nicht die unterordner)
   * @param env_dir envoriment dir (need readwrite rights on it)
   * @param dir directory in witch the files wil be removed (not including sub directorys)
   * @param regex_filter regex for filter filename for delete (ATTENTION, g=global delets only every second file, g is not needed see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/test#using_test_on_a_regex_with_the_global_flag)
   * @return {string[]} aray with names of deleted files
   */
  removeFilesByFilter(
    env_dir: EnvDir,
    dir: string,
    regex_filter: RegExp
  ): Promise<string[]>;
  writeToFile(
    env_dir: EnvDir,
    dir: string,
    file: string,
    data: Blob,
    append: boolean,
    progressEv?: (event: ProgressEvent) => void
  ): Promise<void>;
  readFromFile(
    env_dir: EnvDir,
    dir: string,
    file: string,
    progressEv?: (event: ProgressEvent) => void
  ): Promise<ArrayBuffer>;
  writeStringToFile(
    env_dir: EnvDir,
    dir: string,
    file: string,
    data: string,
    append: boolean,
    progressEv?: (event: ProgressEvent) => void
  ): Promise<void>;
  readStringFromFile(
    env_dir: EnvDir,
    dir: string,
    file: string,
    progressEv?: (event: ProgressEvent) => void
  ): Promise<string>;
  // Overloading for using FileSystemEntries
  getAllEntriesByEntry(directoryEntry: DirectoryEntry): Promise<Entry[]>;
  getAllFilesByEntry(directoryEntry: DirectoryEntry): Promise<FileEntry[]>;
  getAllSubDirectoriesByEntry(
    directoryEntry: DirectoryEntry
  ): Promise<DirectoryEntry[]>;
  removeFileByEntry(fileEntry: FileEntry): Promise<boolean>;
  removeAllFilesByEntry(directoryEntry: DirectoryEntry): Promise<boolean>;
  removeDirectoryByEntry(directoryEntry: DirectoryEntry): Promise<boolean>;
  removeAllSubDirectoriesByEntry(
    directoryEntry: DirectoryEntry
  ): Promise<boolean>;
  writeToFileByEntry(
    fileEntry: FileEntry,
    data: Blob,
    append: boolean,
    progressEv?: (event: ProgressEvent) => void
  ): Promise<void>;
  readFromFileByEntry(
    fileEntry: FileEntry,
    progressEv?: (event: ProgressEvent) => void
  ): Promise<ArrayBuffer>;
  writeStringToFileByEntry(
    fileEntry: FileEntry,
    data: string,
    append: boolean,
    progressEv?: (event: ProgressEvent) => void
  ): Promise<void>;
  readStringFromFileByEntry(
    fileEntry: FileEntry,
    progressEv?: (event: ProgressEvent) => void
  ): Promise<string>;
}

/** Speicher Bereich/Ordner */
export const enum EnvDir {
  /** Yourapp Assets Directory. ATTENTION! is Read-only */
  Assets = 0, // applicationDirectory()/www/assets !read-only!
  /** Data Directory. Persistent storage, can cleard by user */
  Data = 1, // dataDirectory()
  /** Cache Directory. Cached storage, can cleard by user or device */
  Cache = 2, // cacheDirectory()
}

declare let WkWebView: any

/** Standardimplementierung */
export class FileSystemDefault implements IFileSystem {
  private _assetsDir: DirectoryEntry = null;
  private _dataDir: DirectoryEntry = null;
  private _cacheDir: DirectoryEntry = null;

  /** Strukture of Object is: obj[env_dir][dir][file or '\*'] = entry ('\*' used to storage the entry for root dir) */
  private _cachedEntrys: {
    [env_dir: number]: { [dir: string]: { [file: string]: any } };
  } = {};
  private _useCachedEntrys = true;

  /** Gets local dir path as URL
   * @param env_dir EnvDir type
   * @param env_dir bypass if true, the browser path resolve will be used to get a path that is valid for a webbrowser.
   * @returns null or local path dir as URL string
   */
  private static GetLocalDirpath(env_dir: EnvDir, bypass : boolean = false): string {
    return DirectoryPathProvider.provide(bypass).resolve(env_dir);
  }

  /**
   * Creates a path for a relative ressource for a specific cordova file directory.
   * @param relativePathToFile the relative path to the file
   * @param env_dir the cordova specific destination of the root folder for that file
   * @returns An absolute path for the current platform to the relative file
   */
  public static CreatePathForFile(relativePathToFile: string, env_dir: EnvDir): string {
    let forceBrowserResolverForStaticImage = PlatformDetector.isAndroid();
    let url = FileSystemDefault.GetLocalDirpath(env_dir, forceBrowserResolverForStaticImage) + relativePathToFile;
    return FileSystemDefault.ConvertPath(url);
  }

  /**
   * With the new cordova-ios the scheme has changed to app: and therefore the path must be converted.
   * Other paths for other platforms remain the same.
   * @param url An url to be processed
   * @returns The converted path if necessary otherwise the original input data
   */
  public static ConvertPath(url: string): string {
    return (PlatformDetector.isIOS() && WkWebView) ? WkWebView.convertFilePath(url) : url;
  }

  /** Returns the ErrorMessage of FileError Code
   * @param error FileError
   * @returns FileError Code as ErrorMessage
   */
  private static GetFileErrorMessage(error: FileError): string {
    let msg = "";

    switch (error.code) {
      case FileError.NOT_FOUND_ERR:
        msg = "File not found";
        break;
      case FileError.SECURITY_ERR:
        msg = "Security error";
        break;
      case FileError.ABORT_ERR:
        msg = "Abort error";
        break;
      case FileError.NOT_READABLE_ERR:
        msg = "File not readable";
        break;
      case FileError.ENCODING_ERR:
        msg = "Encoding error";
        break;
      case FileError.NO_MODIFICATION_ALLOWED_ERR:
        msg = "Modification not allwed";
        break;
      case FileError.INVALID_STATE_ERR:
        msg = "Invalid state";
        break;
      case FileError.SYNTAX_ERR:
        msg = "Syntax error";
        break;
      case FileError.INVALID_MODIFICATION_ERR:
        msg = "Invalid modification";
        break;
      case FileError.QUOTA_EXCEEDED_ERR:
        msg = "Storage quota exceeded";
        break;
      case FileError.TYPE_MISMATCH_ERR:
        msg = "Type mismatch";
        break;
      case FileError.PATH_EXISTS_ERR:
        msg = "Path already exists";
        break;
      default:
        msg = "Unknown errorcode " + error.code;
        break;
    }

    return msg;
  }

  /** UAPP Environment FileSystem
   * [reference/cordova-plugin-file](https://cordova.apache.org/docs/en/latest/reference/cordova-plugin-file)
   * @param _coreLogService CoreLogService
   */
  constructor(private _coreLogService: CoreLogService) {
    // initialisiere cache, for performance
    this._cachedEntrys[EnvDir.Assets] = {};
    this._cachedEntrys[EnvDir.Data] = {};
    this._cachedEntrys[EnvDir.Cache] = {};
  }

  /** Gets DirectoryEntry form local device
   * @param env_dir EnvDir type
   * @returns DirectoryEntry or null when its not set, or undefined when env_dir unknown
   */
  private GetDirEntry(env_dir: EnvDir): DirectoryEntry {
    switch (env_dir) {
      case EnvDir.Assets:
        return this._assetsDir;
      case EnvDir.Data:
        return this._dataDir;
      case EnvDir.Cache:
        return this._cacheDir;
    }
    return undefined;
  }

  /** Gets the local environment dir for EnvDir
   * @param env_dir EnvDir type
   * @returns DirectoryEntry as Promise
   */
  private GetEnvDirEntry(env_dir: EnvDir): Promise<DirectoryEntry> {
    const me = this;
    return new Promise<DirectoryEntry>((resolve, reject) => {
      const DirEntry = me.GetDirEntry(env_dir);
      if (DirEntry === undefined) {
        reject("env_dir is not known.");
      } else if (DirEntry !== null) {
        resolve(DirEntry);
      } else {
        const setDirectoryEntry = (localDirEntry: DirectoryEntry) => {
          switch (env_dir) {
            case EnvDir.Assets:
              me._assetsDir = localDirEntry;
              break;
            case EnvDir.Data:
              me._dataDir = localDirEntry;
              break;
            case EnvDir.Cache:
              me._cacheDir = localDirEntry;
              break;
          }
          resolve(me.GetDirEntry(env_dir));
        };
        const raiseError = (error: FileError) => {
          reject(
            "GetEnvDir>resolveLocalFileSystemURL: " +
            FileSystemDefault.GetFileErrorMessage(error)
          );
        };
        window.resolveLocalFileSystemURL(
          FileSystemDefault.GetLocalDirpath(env_dir),
          setDirectoryEntry,
          raiseError
        );
      }
    });
  }

  /** Prüfung ob der EnvDir readOnly ist
   * @param env_dir EnvDir type
   * @returns true when readonly else false
   */
  public isReadOnly(env_dir: EnvDir): boolean {
    return env_dir === EnvDir.Assets;
  }

  public getAllEntries(env_dir: EnvDir, dir: string): Promise<Entry[]> {
    const me = this;
    return new Promise<Entry[]>((resolve, reject) => {
      me.getDirectory(env_dir, dir)
        .then((directoryEntry: DirectoryEntry) => {
          me.getAllEntriesByEntry(directoryEntry).then(resolve).catch(reject);
        })
        .catch(reject);
    });
  }

  public getDirectory(env_dir: EnvDir, dir: string): Promise<DirectoryEntry> {
    const me = this;
    return new Promise<DirectoryEntry>((resolve, reject) => {
      const cachedDirectoryEntry = me.GetCachedDirectory(env_dir, dir);
      if (cachedDirectoryEntry !== null) {
        resolve(cachedDirectoryEntry);
      } else {
        me.GetEnvDirEntry(env_dir)
          .then((envDirEntry: DirectoryEntry) => {
            if (dir === "") {
              resolve(envDirEntry);
            } else {
              // go deeper
              const SuccessCB = (dirEntry: DirectoryEntry) => {
                resolve(me.SetCachedDirectory(env_dir, dir, dirEntry));
              };
              const ErrorCB = (fileError: FileError) => {
                if (fileError.code === FileError.NOT_FOUND_ERR) {
                  resolve(null); // Directory Not Found
                } else {
                  reject(
                    "getDirectory(" +
                    dir +
                    "): " +
                    FileSystemDefault.GetFileErrorMessage(fileError)
                  );
                }
              };
              envDirEntry.getDirectory(
                dir,
                { create: false, exclusive: false },
                SuccessCB,
                ErrorCB
              );
            }
          })
          .catch(reject);
      }
    });
  }

  public getAllSubDirectories(
    env_dir: EnvDir,
    dir: string
  ): Promise<DirectoryEntry[]> {
    const me = this;
    return new Promise<DirectoryEntry[]>((resolve, reject) => {
      me.getDirectory(env_dir, dir)
        .then((directoryEntry: DirectoryEntry) => {
          me.getAllSubDirectoriesByEntry(directoryEntry)
            .then(resolve)
            .catch(reject);
        })
        .catch(reject);
    });
  }

  public createDirectory(
    env_dir: EnvDir,
    dir: string
  ): Promise<DirectoryEntry> {
    const me = this;
    return new Promise<DirectoryEntry>((resolve, reject) => {
      const cachedDirectoryEntry = me.GetCachedDirectory(env_dir, dir);
      if (cachedDirectoryEntry !== null) {
        resolve(cachedDirectoryEntry);
      } else {
        me.CreateDirectories(env_dir, dir)
          .then((directoryEntry: DirectoryEntry) => {
            resolve(me.SetCachedDirectory(env_dir, dir, directoryEntry));
          })
          .catch(reject);
      }
    });
  }

  public removeDirectory(env_dir: EnvDir, dir: string): Promise<boolean> {
    const me = this;
    return new Promise<boolean>((resolve, reject) => {
      me.getDirectory(env_dir, dir)
        .then((directoryEntry: DirectoryEntry) => {
          me.removeDirectoryByEntry(directoryEntry).then(resolve).catch(reject);
        })
        .catch(reject);
    });
  }

  public removeAllSubDirectories(
    env_dir: EnvDir,
    dir: string
  ): Promise<boolean> {
    const me = this;
    return new Promise<boolean>((resolve, reject) => {
      me.getDirectory(env_dir, dir)
        .then((directoryEntry: DirectoryEntry) => {
          me.removeAllSubDirectoriesByEntry(directoryEntry)
            .then(resolve)
            .catch(reject);
        })
        .catch(reject);
    });
  }

  public getFile(
    env_dir: EnvDir,
    dir: string,
    file: string
  ): Promise<FileEntry> {
    const me = this;
    return new Promise<FileEntry>((resolve, reject) => {
      const cachedFileEntry = me.GetCachedFile(env_dir, dir, file);
      if (cachedFileEntry !== null) {
        resolve(cachedFileEntry);
      } else {
        me.GetEnvDirEntry(env_dir)
          .then((envDirEntry: DirectoryEntry) => {
            const filePath = dir === "" ? file : dir + "/" + file;
            const SuccessCB = (fileEntry: FileEntry) => {
              resolve(me.SetCachedFile(env_dir, dir, file, fileEntry));
            };
            const ErrorCB = (fileError: FileError) => {
              if (fileError.code === FileError.NOT_FOUND_ERR) {
                resolve(null); // File Not Found
              } else {
                reject(
                  "getFile(" +
                  filePath +
                  "): " +
                  FileSystemDefault.GetFileErrorMessage(fileError)
                );
              }
            };
            envDirEntry.getFile(
              filePath,
              { create: false, exclusive: false },
              SuccessCB,
              ErrorCB
            );
          })
          .catch(reject);
      }
    });
  }

  public getAllFiles(env_dir: EnvDir, dir: string): Promise<FileEntry[]> {
    const me = this;
    return new Promise<FileEntry[]>((resolve, reject) => {
      me.getDirectory(env_dir, dir)
        .then((directoryEntry: DirectoryEntry) => {
          me.getAllFilesByEntry(directoryEntry).then(resolve).catch(reject);
        })
        .catch(reject);
    });
  }

  public createFile(
    env_dir: EnvDir,
    dir: string,
    file: string
  ): Promise<FileEntry> {
    const me = this;
    return new Promise<FileEntry>((resolve, reject) => {
      const cachedFileEntry = me.GetCachedFile(env_dir, dir, file);
      if (cachedFileEntry !== null) {
        resolve(cachedFileEntry);
      } else {
        me.CreateDirectories(env_dir, dir)
          .then((dirEntry: DirectoryEntry) => {
            const SuccessCB = (fileEntry: FileEntry) => {
              resolve(me.SetCachedFile(env_dir, dir, file, fileEntry));
            };
            const ErrorCB = (fileError: FileError) => {
              reject(
                "createFile(" +
                dir +
                "/" +
                file +
                "): " +
                FileSystemDefault.GetFileErrorMessage(fileError)
              );
            };
            dirEntry.getFile(
              file,
              { create: true, exclusive: false },
              SuccessCB,
              ErrorCB
            );
          })
          .catch(reject);
      }
    });
  }

  public removeFile(
    env_dir: EnvDir,
    dir: string,
    file: string
  ): Promise<boolean> {
    const me = this;
    return new Promise<boolean>((resolve, reject) => {
      me.getFile(env_dir, dir, file)
        .then((fileEntry: FileEntry) => {
          me.removeFileByEntry(fileEntry).then(resolve).catch(reject);
        })
        .catch(reject);
    });
  }

  /** löscht mehrere dateien in einem ordner, dessen namen einem regex filter entsprechen (geht prüft nicht die unterordner)
   * @param env_dir envoriment dir (need readwrite rights on it)
   * @param dir directory in witch the files wil be removed (not including sub directorys)
   * @param regex_filter regex for filter filename for delete (ATTENTION, g=global delets only every second file, g is not needed see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/test#using_test_on_a_regex_with_the_global_flag)
   * @return {string[]} aray with names of deleted files
   */
  public removeFilesByFilter(
    env_dir: EnvDir,
    dir: string,
    regex_filter: RegExp
  ): Promise<string[]> {
    const me = this;
    return new Promise<string[]>((resolve, reject) => {
      me.getAllFiles(env_dir, dir)
        .then(async (fileEntries: FileEntry[]) => {
          try {
            const deletedFiles = new Array<string>();
            for (let i = 0; i < fileEntries.length; ++i) {
              const fileEntry = fileEntries[i];
              if (regex_filter.test(fileEntry.name)) {
                deletedFiles.push(fileEntry.name);
                await me.removeFileByEntry(fileEntry);
              }
            }
            resolve(deletedFiles);
          } catch (error) {
            reject(error);
          }
        })
        .catch(reject);
    });
  }

  public removeAllFiles(env_dir: EnvDir, dir: string): Promise<boolean> {
    const me = this;
    return new Promise<boolean>((resolve, reject) => {
      me.getDirectory(env_dir, dir)
        .then((directoryEntry: DirectoryEntry) => {
          me.removeAllFilesByEntry(directoryEntry).then(resolve).catch(reject);
        })
        .catch(reject);
    });
  }

  public writeToFile(
    env_dir: EnvDir,
    dir: string,
    file: string,
    data: Blob,
    append: boolean,
    progressEv?: (event: ProgressEvent) => void
  ): Promise<void> {
    const me = this;
    return new Promise<void>((resolve, reject) => {
      me.getFile(env_dir, dir, file)
        .then((fileEntry: FileEntry) => {
          me.writeToFileByEntry(fileEntry, data, append, progressEv)
            .then(resolve)
            .catch(reject);
        })
        .catch(reject);
    });
  }

  public readFromFile(
    env_dir: EnvDir,
    dir: string,
    file: string,
    progressEv?: (event: ProgressEvent) => void
  ): Promise<ArrayBuffer> {
    const me = this;
    return new Promise<ArrayBuffer>((resolve, reject) => {
      me.getFile(env_dir, dir, file)
        .then((fileEntry: FileEntry) => {
          me.readFromFileByEntry(fileEntry, progressEv)
            .then(resolve)
            .catch(reject);
        })
        .catch(reject);
    });
  }

  public writeStringToFile(
    env_dir: EnvDir,
    dir: string,
    file: string,
    data: string,
    append: boolean,
    progressEv?: (event: ProgressEvent) => void
  ): Promise<void> {
    const me = this;
    return new Promise<void>((resolve, reject) => {
      me.getFile(env_dir, dir, file)
        .then((fileEntry: FileEntry) => {
          me.writeStringToFileByEntry(fileEntry, data, append, progressEv)
            .then(resolve)
            .catch(reject);
        })
        .catch(reject);
    });
  }

  public readStringFromFile(
    env_dir: EnvDir,
    dir: string,
    file: string,
    progressEv?: (event: ProgressEvent) => void
  ): Promise<string> {
    const me = this;
    return new Promise<string>((resolve, reject) => {
      me.getFile(env_dir, dir, file)
        .then((fileEntry: FileEntry) => {
          me.readStringFromFileByEntry(fileEntry, progressEv)
            .then(resolve)
            .catch(reject);
        })
        .catch(reject);
    });
  }

  public getAllEntriesByEntry(
    directoryEntry: DirectoryEntry
  ): Promise<Entry[]> {
    const me = this;
    return new Promise<Entry[]>((resolve, reject) => {
      const directoryReader = directoryEntry.createReader();
      const ErrorCB = (fileError: FileError) => {
        reject(
          "getAllEntries(" +
          directoryEntry.name +
          "): " +
          FileSystemDefault.GetFileErrorMessage(fileError)
        );
      };
      directoryReader.readEntries(resolve, ErrorCB);
    });
  }

  public getAllFilesByEntry(
    directoryEntry: DirectoryEntry
  ): Promise<FileEntry[]> {
    const me = this;
    return new Promise<FileEntry[]>((resolve, reject) => {
      me.getAllEntriesByEntry(directoryEntry)
        .then((entries: Entry[]) => {
          const FileEntries: FileEntry[] = [];
          entries.forEach((entry: Entry) => {
            if (entry.isFile) {
              FileEntries.push(<FileEntry>entry);
            }
          });
          resolve(FileEntries);
        })
        .catch(reject);
    });
  }

  public getAllSubDirectoriesByEntry(
    directoryEntry: DirectoryEntry
  ): Promise<DirectoryEntry[]> {
    const me = this;
    return new Promise<DirectoryEntry[]>((resolve, reject) => {
      me.getAllEntriesByEntry(directoryEntry)
        .then((entries: Entry[]) => {
          const DirectoryEntries: DirectoryEntry[] = [];
          entries.forEach((entry: Entry) => {
            if (entry.isDirectory) {
              DirectoryEntries.push(<DirectoryEntry>entry);
            }
          });
          resolve(DirectoryEntries);
        })
        .catch(reject);
    });
  }

  public removeFileByEntry(fileEntry: FileEntry): Promise<boolean> {
    const me = this;
    return new Promise<boolean>((resolve, reject) => {
      const SuccessCB = () => {
        // todo remove fileentry from cache
        me.RemoveCachedFile(fileEntry);
        resolve(true);
      };
      const ErrorCB = (fileError: FileError) => {
        if (fileError.code === FileError.NOT_FOUND_ERR) {
          resolve(true); // Directory Not Found so is deleted
        } else {
          reject(
            "removeFile(" +
            fileEntry.name +
            "): " +
            FileSystemDefault.GetFileErrorMessage(fileError)
          );
        }
      };
      fileEntry.remove(SuccessCB, ErrorCB);
    });
  }

  /** TODO momently it rais many errors
   * @param env_dir EnvDir type
   * @param dir Directorypath
   */
  public removeAllFilesByEntry(
    directoryEntry: DirectoryEntry
  ): Promise<boolean> {
    const me = this;
    return new Promise<boolean>((resolve, reject) => {
      me.getAllEntriesByEntry(directoryEntry)
        .then((fileEntries: FileEntry[]) => {
          const totalCount = fileEntries.length;
          let deleted = 0;
          let success = true;
          const SuccessCB = () => {
            // todo momentan wird der ganze ordner aus dem cache entfernt, eventeuell nur die files (das ist schneller)
            me.RemoveCachedDirectory(directoryEntry);
            if (++deleted === totalCount) {
              resolve(success);
            }
          };
          const ErrorCB = (fileError: FileError) => {
            if (fileError.code === FileError.NOT_FOUND_ERR) {
              SuccessCB(); // File not found, so it is deleted
            } else {
              success = false;
              reject(
                "removeAllFiles(" +
                directoryEntry.name +
                "): " +
                FileSystemDefault.GetFileErrorMessage(fileError)
              );
            }
          };
          fileEntries.forEach((fileEntry) => {
            fileEntry.remove(SuccessCB, ErrorCB);
          });
        })
        .catch(reject);
    });
  }

  public removeDirectoryByEntry(
    directoryEntry: DirectoryEntry
  ): Promise<boolean> {
    const me = this;
    return new Promise<boolean>((resolve, reject) => {
      const SuccessCB = () => {
        me.RemoveCachedDirectory(directoryEntry);
        resolve(true);
      };
      const ErrorCB = (fileError: FileError) => {
        if (fileError.code === FileError.NOT_FOUND_ERR) {
          resolve(true); // Directory Not Found so is deleted
        } else {
          reject(
            "removeDirectory(" +
            directoryEntry.name +
            "): " +
            FileSystemDefault.GetFileErrorMessage(fileError)
          );
        }
      };
      directoryEntry.remove(SuccessCB, ErrorCB);
    });
  }

  public removeAllSubDirectoriesByEntry(
    directoryEntry: DirectoryEntry
  ): Promise<boolean> {
    const me = this;
    return new Promise<boolean>((resolve, reject) => {
      const SuccessCB = () => {
        // todo momentan wird der ganze ordner aus dem cache entfernt, eventeuell nur die subdirs (das ist schneller)
        me.RemoveCachedDirectory(directoryEntry);
        resolve(true);
      };
      const ErrorCB = (fileError: FileError) => {
        if (fileError.code === FileError.NOT_FOUND_ERR) {
          resolve(true); // Directory Not Found so is deleted
        } else {
          reject(
            "removeAllSubDirectories(" +
            directoryEntry.name +
            "): " +
            FileSystemDefault.GetFileErrorMessage(fileError)
          );
        }
      };
      directoryEntry.removeRecursively(SuccessCB, ErrorCB);
    });
  }

  public writeToFileByEntry(
    fileEntry: FileEntry,
    data: Blob,
    append: boolean,
    progressEv?: (event: ProgressEvent) => void
  ): Promise<void> {
    return this._writeToFileByEntry(fileEntry, false, data, append, progressEv);
  }

  public readFromFileByEntry(
    fileEntry: FileEntry,
    progressEv?: (event: ProgressEvent) => void
  ): Promise<ArrayBuffer> {
    return new Promise<ArrayBuffer>((resolve, reject) => {
      this._readFromFileByEntry(fileEntry, false, progressEv)
        .then((data) => {
          resolve(<ArrayBuffer>data);
        })
        .catch(reject);
    });
  }

  public writeStringToFileByEntry(
    fileEntry: FileEntry,
    data: string,
    append: boolean,
    progressEv?: (event: ProgressEvent) => void
  ): Promise<void> {
    return this._writeToFileByEntry(fileEntry, true, data, append, progressEv);
  }

  public readStringFromFileByEntry(
    fileEntry: FileEntry,
    progressEv?: (event: ProgressEvent) => void
  ): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this._readFromFileByEntry(fileEntry, true, progressEv)
        .then((data) => {
          resolve(<string>data);
        })
        .catch(reject);
    });
  }

  private _writeToFileByEntry(
    fileEntry: FileEntry,
    asString: boolean,
    data: string | Blob,
    append: boolean,
    progressEv?: (event: ProgressEvent) => void
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const SuccessCB = (writer: FileWriter) => {
        if (progressEv) {
          writer.onwritestart = progressEv;
          writer.onprogress = progressEv;
          writer.onwrite = progressEv;
        }
        writer.onwriteend = (event: ProgressEvent) => {
          if (progressEv) {
            progressEv(event);
          }
          resolve();
        };
        writer.onabort = () => {
          reject("writeToFile(" + fileEntry.name + ") onabort");
        };
        writer.onerror = () => {
          reject("writeToFile(" + fileEntry.name + ") onerror");
        };

        if (append) {
          // jump to end of file
          writer.position = writer.length;
        }

        // todo um platz zu sparen könnte inhalt auch gezipt gespeichert werden (die NodeJS function beachten für get_server_data)
        let blobData: Blob = null;
        if (asString) {
          blobData = new Blob([data], { type: "text/plain" });
        } else {
          blobData = <Blob>data;
        }

        writer.write(blobData);
      };
      const ErrorCB = (fileError: FileError) => {
        reject(
          "writeToFile(" +
          fileEntry.name +
          "): " +
          FileSystemDefault.GetFileErrorMessage(fileError)
        );
      };
      fileEntry.createWriter(SuccessCB, ErrorCB);
    });
  }

  private _readFromFileByEntry(
    fileEntry: FileEntry,
    asString: boolean,
    progressEv?: (event: ProgressEvent) => void
  ): Promise<string | ArrayBuffer> {
    return new Promise<string | ArrayBuffer>((resolve, reject) => {
      const SuccessCB = (file: File) => {
        const fileReader = new FileReader();
        if (progressEv) {
          fileReader.onprogress = progressEv;
          fileReader.onloadstart = progressEv;
          fileReader.onload = progressEv;
        }
        fileReader.onloadend = (event: ProgressEvent) => {
          if (progressEv) {
            progressEv(event);
          }

          resolve(fileReader.result);
        };
        fileReader.onabort = () => {
          reject("readFromFile(" + file.name + ") onabort");
        };
        fileReader.onerror = () => {
          reject("readFromFile(" + file.name + ") onerror");
        };

        if (asString) {
          fileReader.readAsText(file);
        } else {
          fileReader.readAsBinaryString(file);
        }
      };
      const ErrorCB = (fileError: FileError) => {
        reject(
          "file(" +
          fileEntry.name +
          "): " +
          FileSystemDefault.GetFileErrorMessage(fileError)
        );
      };
      fileEntry.file(SuccessCB, ErrorCB);
    });
  }

  /** Create dirs recoursive (function not cache the directories)
   * @param env_dir EnvDir type
   * @param dir Directory path like "hg/wg/ug"
   * @returns DirectoryEntry from the last dir
   */
  private CreateDirectories(
    env_dir: EnvDir,
    dir: string
  ): Promise<DirectoryEntry> {
    const me = this;
    return new Promise<DirectoryEntry>((resolve, reject) => {
      const cachedFileEntry = me.GetCachedDirectory(env_dir, dir);
      if (cachedFileEntry !== null) {
        resolve(cachedFileEntry);
      } else {
        me.GetEnvDirEntry(env_dir)
          .then((envDirEntry: DirectoryEntry) => {
            if (dir === "") {
              resolve(envDirEntry);
            }
            const dirs = dir.split("/");
            const CreateDirectory = (
              dirList: string[],
              position: number,
              parentDirEntry: DirectoryEntry
            ) => {
              const curDir = dirList[position];
              const SuccessCB = (dirEntry: DirectoryEntry) => {
                if (++position === dirList.length) {
                  resolve(dirEntry);
                } else {
                  CreateDirectory(dirs, position, dirEntry); // recursive call
                }
              };
              const ErrorCB = (fileError: FileError) => {
                reject(
                  "createDirectories(" +
                  dir +
                  "): " +
                  FileSystemDefault.GetFileErrorMessage(fileError)
                );
              };
              parentDirEntry.getDirectory(
                curDir,
                { create: true, exclusive: false },
                SuccessCB,
                ErrorCB
              );
            };
            CreateDirectory(dirs, 0, envDirEntry);
          })
          .catch(reject);
      }
    });
  }

  // Helper vor cache
  private _GetCachedEntry(env_dir: EnvDir, dir: string, file: string): Entry {
    if (!this._useCachedEntrys) {
      return null;
    }
    let result: Entry = null;

    const cachedDir = this._cachedEntrys[env_dir][dir];
    if (typeof cachedDir !== "undefined") {
      const cachedEntry = cachedDir[file];
      if (typeof cachedEntry !== "undefined") {
        result = cachedEntry;
      }
    }
    return result;
  }

  private _SetCachedEntry(
    env_dir: EnvDir,
    dir: string,
    file: string,
    entry: Entry
  ): Entry {
    if (!this._useCachedEntrys) {
      return;
    }
    let cachedDir = null;
    const cachedEnvDir = this._cachedEntrys[env_dir];
    cachedDir = cachedEnvDir[dir];
    if (typeof cachedDir === "undefined") {
      cachedDir = cachedEnvDir[dir] = {};
    }
    return (cachedDir[file] = entry);
  }

  private _RemoveCachedEntry(entry: Entry): void {
    if (!this._useCachedEntrys) {
      return null; // break, no cache used
    }
    const cachedEntryPath = this._GetCachedEntryPath(entry);
    if (cachedEntryPath != null) {
      if (entry.isDirectory && cachedEntryPath.file === "*") {
        delete this._cachedEntrys[cachedEntryPath.env_dir][cachedEntryPath.dir]; // delete dir and all files in it
      } else if (entry.isFile && cachedEntryPath.file !== "*") {
        const cachedDir =
          this._cachedEntrys[cachedEntryPath.env_dir][cachedEntryPath.dir];
        if (typeof cachedDir !== "undefined") {
          delete cachedDir[cachedEntryPath.file];
        }
      } else {
        this._coreLogService.error(
          "FileSystemDefault",
          "entry not equal with path",
          entry,
          cachedEntryPath
        );
      }
    }
  }

  private _GetCachedEntryPath(entry: Entry): {
    env_dir: number;
    dir: string;
    file: string;
  } {
    if (!this._useCachedEntrys) {
      return null;
    }
    // durch laufen der _cachedEntrys um den gesuchten eintrag zufinden,danach wird der pfad zurückgegeben
    for (const env_dir_key in this._cachedEntrys) {
      if (this._cachedEntrys.hasOwnProperty(env_dir_key)) {
        const env_dir = parseInt(env_dir_key, 10);
        const dirs = this._cachedEntrys[env_dir];
        for (const dir in dirs) {
          if (dirs.hasOwnProperty(dir)) {
            const files = dirs[dir];
            if (typeof files !== undefined) {
              if (entry.isDirectory) {
                if (files["*"] === entry) {
                  // searched entry found, break here
                  return { env_dir: env_dir, dir: dir, file: "*" };
                }
              } else if (entry.isFile) {
                for (const file in files) {
                  if (files[file] === entry) {
                    // searched entry found, break here
                    return { env_dir: env_dir, dir: dir, file: file };
                  }
                }
              }
            }
          }
        }
      }
    }
    // null if entry not found
    return null;
  }

  private GetCachedDirectory(env_dir: EnvDir, dir: string): DirectoryEntry {
    return <DirectoryEntry>this._GetCachedEntry(env_dir, dir, "*");
  }

  private SetCachedDirectory(
    env_dir: EnvDir,
    dir: string,
    entry: DirectoryEntry
  ): DirectoryEntry {
    return <DirectoryEntry>this._SetCachedEntry(env_dir, dir, "*", entry);
  }

  private RemoveCachedDirectory(entry: DirectoryEntry): void {
    this._RemoveCachedEntry(entry);
  }

  private GetCachedFile(env_dir: EnvDir, dir: string, file: string): FileEntry {
    return <FileEntry>this._GetCachedEntry(env_dir, dir, file);
  }

  private SetCachedFile(
    env_dir: EnvDir,
    dir: string,
    file: string,
    entry: FileEntry
  ): FileEntry {
    return <FileEntry>this._SetCachedEntry(env_dir, dir, file, entry);
  }

  private RemoveCachedFile(entry: FileEntry): void {
    this._RemoveCachedEntry(entry);
  }
}
